import {
  TERMS_AND_CONDITIONS,
  type TermsAndConditionsEntry,
  type TermsAndConditionsLink,
  type TNCKey,
} from '@repo/shared-config/terms-and-conditions';

export type { TNCKey, TermsAndConditionsEntry, TermsAndConditionsLink };

/**
 * Resolves the correct Terms & Conditions URL for a given document key,
 * based on the user's language and/or legal country.
 *
 * @param key - The T&C document identifier (e.g. `"qonto-privacy-policy"`)
 * @param options - Options object with `language` (user's app language, e.g. "fr", "en") and `country` (user's legal country code, e.g. "FR", "DE")
 * @returns The resolved URL, or `null` if the document is not available for the user.
 */
export function getTNCLink(
  key: TNCKey,
  options: { language?: string; country?: string }
): string | null {
  const entry = TERMS_AND_CONDITIONS[key];

  if (entry.type === 'language') {
    return resolveByLanguage(entry.links, options.language);
  }

  return resolveByCountry(entry.links, options.country, options.language);
}

function resolveByLanguage(links: TermsAndConditionsLink[], language?: string): string | null {
  if (language) {
    const match = links.find(link => link.language === language);
    if (match) return match.url;
  }

  const englishFallback = links.find(link => link.language === 'en');
  if (englishFallback) return englishFallback.url;

  const first = links[0];
  return first?.url ?? null;
}

function resolveByCountry(
  links: TermsAndConditionsLink[],
  country?: string,
  language?: string
): string | null {
  if (!country) {
    throw new Error('Missing country for country-type T&C lookup');
  }

  const countryLinks = links.filter(link => link.country === country);

  if (countryLinks.length === 0) {
    return null;
  }

  const hasLanguageField = countryLinks.some(link => link.language !== undefined);

  if (!hasLanguageField) {
    return countryLinks[0]?.url ?? null;
  }

  return resolveByLanguage(countryLinks, language);
}
